import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Space, Breadcrumb, Button, Input, Switch, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import type { ColumnsType } from "antd/es/table";

import type {
  ListDeviceProfileVendorsResponse,
  DeviceProfileVendorListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import {
  GetDeviceProfileVendorRequest,
  ListDeviceProfileVendorsRequest,
  UpdateDeviceProfileVendorRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";

import type { GetPageCallbackFunc } from "../../components/DataTable";
import DataTable from "../../components/DataTable";
import deviceProfileStore from "../../stores/DeviceProfileStore";
import { useTitle } from "../helpers";

function ListDeviceProfileVendors() {
  useTitle("Network Server", "Device Profiles", "Vendors");
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // The list endpoint returns a list-item shape that omits some fields
  // (ouis, metadata, vendor_id) needed to build a full Update payload.
  // We GET the full vendor first, flip published, and PUT it back. Backend
  // short-circuits repo-source rows to published-only, so non-published
  // fields never round-trip incorrectly.
  const togglePublished = (id: string, next: boolean) => {
    setTogglingId(id);
    const getReq = new GetDeviceProfileVendorRequest();
    getReq.setId(id);
    deviceProfileStore.getVendor(getReq, resp => {
      const v = resp.getVendor();
      if (!v) {
        setTogglingId(null);
        return;
      }
      v.setPublished(next);
      const updReq = new UpdateDeviceProfileVendorRequest();
      updReq.setVendor(v);
      deviceProfileStore.updateVendor(updReq, () => {
        setTogglingId(null);
        setRefreshKey(k => k + 1);
      });
    });
  };

  const columns: ColumnsType<DeviceProfileVendorListItem.AsObject> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => <Link to={`/device-profiles/vendors/${record.id}/devices`}>{text}</Link>,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 120,
      render: source =>
        source === "repo" ? <Tag color="blue">Repo</Tag> : <Tag color="green">Bridge</Tag>,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      width: 120,
      render: (_published, record) => (
        <Switch
          checked={record.published}
          loading={togglingId === record.id}
          onChange={next => togglePublished(record.id, next)}
        />
      ),
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    _filters: object,
    _orderBy: string | void,
    _orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    const req = new ListDeviceProfileVendorsRequest();
    req.setLimit(limit);
    req.setOffset(offset);
    req.setSearch(search);

    deviceProfileStore.listVendors(req, (resp: ListDeviceProfileVendorsResponse) => {
      const obj = resp.toObject();
      callbackFunc(obj.totalCount, obj.resultList);
    });
  };

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb items={[{ title: "Network Server" }, { title: "Device Profiles" }, { title: "Vendors" }]} />
        )}
        title="Vendors"
        extra={[
          <Input.Search
            key="search-vendors"
            placeholder="Search by name"
            allowClear
            onSearch={value => {
              setSearch(value);
              setRefreshKey(k => k + 1);
            }}
            style={{ width: 240 }}
          />,
          <Button key="add-vendor" type="primary" onClick={() => navigate("/device-profiles/vendors/create")}>
            Add vendor
          </Button>,
        ]}
      />
      <DataTable columns={columns} getPage={getPage} rowKey="id" refreshKey={refreshKey} />
    </Space>
  );
}

export default ListDeviceProfileVendors;
